import React from 'react';
import { startRegistration } from '@simplewebauthn/browser';
import { Link } from 'react-router-dom';
import type { Me } from '../App';
import { api } from '../api';
import { useToast } from '../ui/Toast';

export function CalDavInfoPage({ me }: { me: Me }) {
  const toast = useToast();
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const principalUrl = `${origin}/caldav/${encodeURIComponent(me.email)}/`;

  const enrollPasskey = async () => {
    try {
      const opts = await api.post<never>('/api/auth/passkey/register/options');
      const response = await startRegistration(opts);
      await api.post('/api/auth/passkey/register/verify', { response, deviceLabel: navigator.userAgent });
      toast.success('Passkey registriert');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Passkey-Registrierung fehlgeschlagen');
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="card">
        <h1 className="text-h3 mb-2">CalDAV-Zugang</h1>
        <p className="text-body text-ink-secondary mb-4">
          Trage diese Verbindungsdaten in iPhone, eM Client, Android-Kalender oder einen anderen CalDAV-Client ein.
          Als Passwort dient ein App-Passwort, das du unter <strong>App-Passwoerter</strong> erzeugst.
        </p>
        <dl className="grid grid-cols-[200px,1fr] gap-y-3 text-body">
          <dt className="text-ink-muted">Server / Account URL</dt>
          <dd className="font-mono">{principalUrl}</dd>
          <dt className="text-ink-muted">Server-Host</dt>
          <dd className="font-mono">{origin.replace(/^https?:\/\//, '')}</dd>
          <dt className="text-ink-muted">Pfad</dt>
          <dd className="font-mono">/caldav/{me.email}/</dd>
          <dt className="text-ink-muted">Benutzername</dt>
          <dd className="font-mono">{me.email}</dd>
          <dt className="text-ink-muted">Passwort</dt>
          <dd>App-Passwort (siehe Menue links)</dd>
          <dt className="text-ink-muted">Discovery</dt>
          <dd className="font-mono">{origin}/.well-known/caldav</dd>
        </dl>
      </div>

      <div className="card">
        <h2 className="text-h4 mb-2">Passkey/Webauthn</h2>
        <p className="text-body text-ink-secondary mb-3">
          Registriere einen Passkey, damit du dich ohne Passwort einloggen kannst. Empfohlen: einen pro Geraet.
        </p>
        <button className="btn btn-primary" onClick={enrollPasskey}>+ Passkey hinzufuegen</button>
      </div>

      <div className="card">
        <h2 className="text-h4 mb-2">Hinweise zu Clients</h2>
        <ul className="text-body text-ink-secondary space-y-2 list-disc pl-5">
          <li>iOS Kalender: Einstellungen → Kalender → Account hinzufuegen → CalDAV.</li>
          <li>eM Client: Menue → Konto → CalDAV/CardDAV → CalDAV. Als Server/URL die komplette Account-URL oben verwenden.</li>
          <li>Android: Apps wie "DAVx5" benutzen; URL und App-Passwort wie oben angeben.</li>
        </ul>
      </div>

      <div className="card">
        <h2 className="text-h4 mb-2">eM Client Checkliste</h2>
        <div className="grid gap-3 text-body text-ink-secondary">
          <div>
            <div className="label">URL</div>
            <div className="font-mono text-ink-primary break-all">{principalUrl}</div>
          </div>
          <div>
            <div className="label">Benutzername</div>
            <div className="font-mono text-ink-primary">{me.email}</div>
          </div>
          <div>
            <div className="label">Passwort</div>
            <div>Ein aktives App-Passwort, nicht dein normales Web-Login-Passwort.</div>
          </div>
          {me.role === 'ADMIN' && (
            <div className="rounded-md border border-border bg-surface-elevated p-3">
              Nach einem Verbindungsversuch siehst du unter <Link className="text-brand-primary hover:underline" to="/admin">Admin → CalDAV-Logs</Link>, ob eM Client OPTIONS/PROPFIND/REPORT sendet und ob die Anmeldung akzeptiert wird.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
